import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, LogOut, Check, Users, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../lib/AuthContext";

const PLAN_META = {
  joh_membership_1x: { label: "1× per week", price: "€40", freq: 1 },
  joh_membership_2x: { label: "2× per week", price: "€65", freq: 2 },
  joh_membership_3x: { label: "Onbeperkt · alle lessen", price: "€80", freq: 999 },
};
const WEEKDAY_NL = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [week, setWeek] = useState([]);
  const [mine, setMine] = useState(new Set());
  const [progress, setProgress] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const email = user?.email;

  const load = useCallback(async () => {
    if (!email) return;
    try {
      const [s, w, m, p, c] = await Promise.all([
        api.get(`/me/summary?email=${encodeURIComponent(email)}`),
        api.get("/classes/upcoming?days_ahead=7"),
        api.get(`/classes/rsvp/mine?email=${encodeURIComponent(email)}`),
        api.get(`/me/progress?email=${encodeURIComponent(email)}`),
        api.get(`/me/coach-update?email=${encodeURIComponent(email)}`),
      ]);
      setSummary(s.data);
      setWeek((w.data.items || []).filter((c) => c.bookable !== false)); // bedrijfssessies zijn nooit boekbaar
      setMine(new Set((m.data.items || []).map((x) => x.instance_id)));
      setProgress(p.data);
      setCoach(c.data);
    } catch (e) {
      toast.error("Kon dashboard niet laden");
    }
    setLoading(false);
  }, [email]);

  useEffect(() => {
    if (user === false) { nav("/login?next=/dashboard", { replace: true }); return; }
    if (!user || user === null) return;
    load();
  }, [user, nav, load]);

  const doSignOut = () => {
    signOut();
    toast.success("Uitgelogd");
    nav("/", { replace: true });
  };

  const book = async (inst) => {
    setBusy(inst.id);
    try {
      const { data } = await api.post("/classes/rsvp", {
        instance_id: inst.id,
        first_name: user.first_name || (user.name || "").split(" ")[0] || "",
        last_name: user.last_name || (user.name || "").split(" ").slice(1).join(" ") || "-",
        email,
      });
      if (data.status === "confirmed" || data.status === "already_rsvpd") {
        setMine((prev) => new Set(prev).add(inst.id));
        toast.success("Geboekt — tot in de ring.");
      } else if (data.status === "waitlisted") {
        toast.success(`Les vol — je staat op de reservelijst (#${data.position}).`);
      }
      load();
    } catch (err) {
      if (err?.response?.status === 401) { nav("/login?next=/dashboard"); return; }
      if (err?.response?.status === 403 && err?.response?.data?.detail === "membership_pending_approval") {
        toast.info("Je membership wacht nog op goedkeuring — zodra die actief is, boek je hier direct.");
        setBusy(null);
        return;
      }
      if (err?.response?.status === 402) {
        toast.error("Geen actief pakket of credits — kies eerst een pakket.", {
          action: { label: "Kies een pakket", onClick: () => nav("/join") },
        });
        setBusy(null);
        return;
      }
      toast.error(formatApiError(err?.response?.data?.detail) || "Boeken mislukt");
    }
    setBusy(null);
  };

  const cancel = async (inst) => {
    setBusy(inst.id);
    try {
      await api.delete(`/classes/rsvp?instance_id=${encodeURIComponent(inst.id)}&email=${encodeURIComponent(email)}`);
      setMine((prev) => { const n = new Set(prev); n.delete(inst.id); return n; });
      toast.success("Afgemeld.");
      load();
    } catch {
      toast.error("Afmelden mislukt");
    }
    setBusy(null);
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/me/portal", { email, return_url: window.location.href });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Portal openen mislukt");
      setPortalBusy(false);
    }
  };

  if (user === null) return null;

  const firstName = user?.first_name || user?.name?.split(" ")?.[0] || "daar";
  const ent = summary?.entitlement;
  const upcoming = summary?.upcoming || [];
  const past = summary?.past || [];
  const next = upcoming[0];
  const curPlan = PLAN_META[ent?.current_plan];
  const upgrades = Object.entries(PLAN_META).filter(([, v]) => v.freq > (curPlan?.freq ?? 0));
  const fmtDate = (iso) => new Date(iso).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  const fmtDay = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return { wd: WEEKDAY_NL[(dt.getUTCDay() + 6) % 7], day: dt.getUTCDate(), month: dt.toLocaleString("nl-NL", { month: "short", timeZone: "UTC" }) };
  };

  return (
    <main className="min-h-screen bg-[#F5F3F0] grain" data-testid="dashboard-page">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between line-b sticky top-0 z-40 bg-[#F5F3F0]/95 backdrop-blur-sm">
        <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover" data-testid="dash-logo">JOH.</Link>
        <div className="flex items-center gap-4">
          <Link to="/mij" className="chip text-[#4A4A4A] accent-hover hidden md:inline-flex items-center gap-1" data-testid="dash-nav-portal">
            Portal <ArrowUpRight size={12}/>
          </Link>
          <button onClick={doSignOut} className="chip text-[#4A4A4A] accent-hover inline-flex items-center gap-1" data-testid="dash-sign-out">
            <LogOut size={14}/> Uitloggen
          </button>
        </div>
      </header>

      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <div className="chip text-[#4A4A4A]">— Dashboard</div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] mt-3 text-[#0A0A0A]"
          data-testid="dash-greeting"
        >
          Welkom terug, {firstName}.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
          className="mt-5 text-[#4A4A4A] text-lg md:text-2xl font-serif-italic"
          data-testid="dash-tagline"
        >
          Ready to put in the work?
        </motion.p>
      </section>

      {/* Profiel compleet maken (spec: alleen ontbrekende gegevens, niet-blokkerend) */}
      {user?.profile_complete === false && (
        <section className="px-6 md:px-10 pb-8" data-testid="dash-profile-incomplete">
          <div className="border-2 p-5 md:p-6 flex flex-wrap items-center gap-4" style={{ borderColor: "var(--joh-accent)", background: "#0A0A0A" }}>
            <div className="flex-1 min-w-60">
              <div className="chip" style={{ color: "var(--joh-accent)" }}>Profiel aanvullen vereist</div>
              <div className="font-display uppercase text-xl md:text-2xl text-[#F5F3F0] mt-1">Maak je profiel compleet</div>
              <p className="text-[#A3A3A3] text-sm mt-1">
                We missen nog: {(user.profile_missing_labels || []).join(", ")}. Nodig om een membership te activeren.
              </p>
            </div>
            <Link to={`/profiel?next=${encodeURIComponent("/dashboard")}`} className="btn" data-testid="dash-complete-profile">
              Aanvullen <ArrowUpRight size={14}/>
            </Link>
          </div>
        </section>
      )}

      {/* Membership-wachtscherm (spec 8): betaling + goedkeuring = automatische activatie */}
      {summary?.membership_gate && !ent?.has_membership && (
        <section className="px-6 md:px-10 pb-8" data-testid="dash-membership-gate">
          <div className="border-2 p-6 md:p-8 bg-[#EAE7E2]" style={{ borderColor: summary.membership_gate.state === "rejected" ? "#F87171" : "var(--joh-accent)" }}>
            <div className="chip" style={{ color: summary.membership_gate.state === "rejected" ? "#B91C1C" : "var(--joh-accent)" }}>
              {summary.membership_gate.state === "rejected" ? "Aanmelding afgewezen" : "Je aanmelding wordt verwerkt"}
            </div>
            <div className="font-display uppercase text-2xl md:text-4xl text-[#0A0A0A] mt-2" data-testid="dash-gate-title">
              {summary.membership_gate.state === "rejected"
                ? "Helaas niet goedgekeurd."
                : summary.membership_gate.payment_done
                  ? "Betaling ontvangen."
                  : summary.membership_gate.approval_done
                    ? "Goedgekeurd."
                    : "Registratie ontvangen."}
            </div>
            <p className="text-[#4A4A4A] mt-2 max-w-xl" data-testid="dash-gate-text">
              {summary.membership_gate.state === "rejected"
                ? "We kunnen je aanmelding helaas niet goedkeuren. Een eventuele betaling is terugbetaald. Vragen? Mail booking@johboxing.com."
                : summary.membership_gate.payment_done
                  ? "Je betaling is ontvangen. Je aanmelding wacht nog op goedkeuring van JOH Boxing — je hoort het zodra je membership actief is."
                  : summary.membership_gate.approval_done
                    ? "Je aanmelding is goedgekeurd. Rond je betaling af om je membership te activeren."
                    : "Je registratie is ontvangen. Zodra je betaling en goedkeuring zijn afgerond, wordt je membership geactiveerd."}
            </p>
            {summary.membership_gate.state === "pending" && !summary.membership_gate.payment_done && (
              <Link to={`/join?plan=${summary.membership_gate.plan || "joh_membership_1x"}`} className="btn mt-5 inline-flex" data-testid="dash-gate-pay">
                Betaling afronden <ArrowUpRight size={14}/>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* Volgende training */}
      <section className="px-6 md:px-10 pb-16" data-testid="dash-next-section">
        <div className="border border-[#0A0A0A] p-6 md:p-8 bg-[#0A0A0A] text-[#F5F3F0]" data-testid="dash-next-card">
          <div className="chip text-[#A3A3A3] mb-2">Eerstvolgende training</div>
          {loading ? (
            <div className="chip text-[#A3A3A3]">Laden…</div>
          ) : next ? (
            <>
              <div className="font-display text-6xl md:text-7xl leading-none text-[#F5F3F0]" data-testid="dash-next-day">
                {new Date(next.class_date).getDate()}
              </div>
              <div className="chip text-[#DDD6CB] mt-2 uppercase">{fmtDate(next.class_date)} · {next.class_start}</div>
              <div className="font-display uppercase text-2xl md:text-3xl mt-4" data-testid="dash-next-type">{next.class_type}</div>
              <div className="chip text-[#A3A3A3] mt-2">{next.credit_type === "membership" ? "Membership" : next.credit_type?.startsWith("joh_pack") ? "Strippenkaart" : "Bevestigd"}</div>
            </>
          ) : (
            <>
              <div className="font-display uppercase text-2xl md:text-3xl">Nog geen training gepland</div>
              <p className="text-[#DDD6CB] mt-2">Boek hieronder direct een les uit het weekrooster.</p>
            </>
          )}
        </div>
      </section>

      {/* Lessen deze week */}
      <section className="px-6 md:px-10 pb-16" id="dash-week" data-testid="dash-week">
        <div className="chip text-[#4A4A4A] mb-2">— Lessen deze week</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl text-[#0A0A0A] mb-8">Boek je training.</h2>
        {loading ? <div className="chip text-[#4A4A4A]">Laden…</div>
        : week.length === 0 ? (
          <div className="border border-[#0A0A0A] p-8 text-center chip text-[#4A4A4A]" data-testid="dash-week-empty">Geen lessen in het rooster deze week.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="dash-week-grid">
            {week.map((c) => {
              const d = fmtDay(c.date);
              const isMine = mine.has(c.id);
              const full = (c.going || 0) >= c.capacity;
              return (
                <div key={c.id} className={`border p-5 flex flex-col justify-between transition-colors ${isMine ? "bg-[#0A0A0A] text-[#F5F3F0] border-[#0A0A0A]" : "bg-[#EAE7E2] text-[#0A0A0A] border-[#0A0A0A]"}`} data-testid={`dash-class-${c.id}`}>
                  <div>
                    <div className={`chip ${isMine ? "text-[#A3A3A3]" : "text-[#4A4A4A]"}`}>{d.wd}</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-display text-4xl leading-none">{d.day}</span>
                      <span className={`chip uppercase ${isMine ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}>{d.month}</span>
                      <span className={`chip ml-auto ${isMine ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}>{c.start}–{c.end}</span>
                    </div>
                    <div className="font-display uppercase text-xl md:text-2xl mt-4">{c.type}</div>
                    <div className={`chip mt-1 ${isMine ? "text-[#A3A3A3]" : "text-[#4A4A4A]"}`}>
                      {c.level}
                      {c.show_count ? ` · ${Math.max(0, c.capacity - (c.going || 0))} plekken` : ""}
                      {full && !isMine ? " · VOL" : ""}
                    </div>
                  </div>
                  {isMine ? (
                    <button onClick={() => cancel(c)} disabled={busy === c.id}
                      className="mt-5 w-full justify-center btn-ghost text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]"
                      data-testid={`dash-booked-${c.id}`}>
                      {busy === c.id ? "…" : <><Check size={14}/> Geboekt — afmelden</>}
                    </button>
                  ) : (
                    <button onClick={() => book(c)} disabled={busy === c.id}
                      className="mt-5 w-full justify-center btn" data-testid={`dash-book-${c.id}`}>
                      {busy === c.id ? "…" : full ? (<><Users size={14}/> Reservelijst</>) : (<><Check size={14}/> Boek deze les</>)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Mijn voortgang + coach update */}
      <section className="px-6 md:px-10 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="border border-[#0A0A0A] p-6 md:p-8 bg-[#EAE7E2]" data-testid="dash-progress">
          <div className="chip text-[#4A4A4A] mb-4 inline-flex items-center gap-2"><TrendingUp size={14}/> Mijn voortgang</div>
          {loading || !progress ? <div className="chip text-[#4A4A4A]">Laden…</div> : progress.attended_total === 0 ? (
            <p className="text-[#4A4A4A]" data-testid="dash-progress-empty">Nog geen geregistreerde aanwezigheid. Boek je eerste les en je voortgang verschijnt hier.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-progress-stats">
                {[
                  ["Trainingen totaal", progress.attended_total, "dash-p-total"],
                  ["Deze maand", progress.this_month, "dash-p-month"],
                  ["Vorige maand", progress.last_month, "dash-p-prev"],
                  ["Weken op rij", progress.streak_weeks, "dash-p-streak"],
                ].map(([label, val, tid]) => (
                  <div key={tid} className="border border-[#0A0A0A]/20 p-3" data-testid={tid}>
                    <div className="font-display text-3xl md:text-4xl text-[#0A0A0A]">{val}</div>
                    <div className="chip text-[#4A4A4A] mt-1">{label}</div>
                  </div>
                ))}
              </div>
              <div className="chip text-[#4A4A4A] mt-4" data-testid="dash-progress-freq">
                Ritme: {progress.per_week_8w}× per week (8 wkn){progress.attendance_pct !== null ? ` · opkomst ${progress.attendance_pct}%` : ""}
              </div>
              {progress.recent?.length > 0 && (
                <div className="mt-6" data-testid="dash-progress-recent">
                  <div className="chip text-[#4A4A4A] mb-2">Recente trainingen</div>
                  <div className="line-t">
                    {progress.recent.map((r, i) => (
                      <div key={i} className="line-b py-2.5 flex flex-wrap items-center gap-3">
                        <span className="chip text-[#4A4A4A] w-28">{r.class_date}</span>
                        <span className="text-sm font-semibold text-[#0A0A0A] flex-1">{r.class_type}{r.jace?.title ? ` · ${r.jace.title}` : ""}</span>
                        {r.jace?.objective && <span className="chip accent-ink" data-testid={`dash-jace-${i}`}>{r.jace.objective}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border border-[#0A0A0A] p-6 md:p-8 bg-[#0A0A0A] text-[#F5F3F0]" data-testid="dash-coach">
          <div className="chip text-[#A3A3A3] mb-4 inline-flex items-center gap-2"><Sparkles size={14}/> Mijn coach update</div>
          {loading || !coach ? <div className="chip text-[#A3A3A3]">Laden…</div> : coach.update ? (
            <>
              <p className="text-lg md:text-xl leading-relaxed text-[#F5F3F0] font-serif-italic" data-testid="dash-coach-text">“{coach.update.text}”</p>
              <div className="chip text-[#4A4A4A] mt-4">{coach.update.month_key}{coach.update.ai ? " · persoonlijk gegenereerd" : ""}</div>
            </>
          ) : (
            <p className="text-[#DDD6CB]" data-testid="dash-coach-placeholder">{coach.placeholder}</p>
          )}
          <a href="#dash-week" className="btn-ghost text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] mt-6 inline-flex" data-testid="dash-coach-cta">
            Bekijk beschikbare lessen <ArrowUpRight size={14}/>
          </a>
        </div>
      </section>

      {/* Mijn pakket */}
      <section className="px-6 md:px-10 pb-16" data-testid="dash-package">
        <div className="border border-[#0A0A0A] p-6 md:p-8 bg-[#EAE7E2]">
          <div className="chip text-[#4A4A4A] mb-4">— Mijn pakket</div>
          {loading ? <div className="chip text-[#4A4A4A]">Laden…</div> : ent?.has_membership ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div>
                <div className="font-display uppercase text-3xl md:text-4xl text-[#0A0A0A]" data-testid="dash-plan-name">
                  {curPlan ? curPlan.label : "Membership actief"}
                </div>
                {curPlan && <div className="chip text-[#4A4A4A] mt-1">{curPlan.price} / maand · actief</div>}
                <p className="chip text-[#4A4A4A] mt-4 max-w-sm" data-testid="dash-downgrade-note">
                  Wil je jouw pakket downgraden? Neem contact met ons op via{" "}
                  <a href="mailto:booking@johboxing.com" className="accent-ink underline" data-testid="dash-downgrade-mail">booking@johboxing.com</a>.
                </p>
              </div>
              {upgrades.length > 0 && (
                <div>
                  <div className="chip mb-3" style={{ color: "var(--joh-accent)" }}>Meer trainen?</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {upgrades.map(([k, v]) => (
                      <button key={k} onClick={openPortal} disabled={portalBusy}
                        className="text-left border border-[#0A0A0A] bg-[#F5F3F0] p-4 hover:bg-[#0A0A0A] hover:text-[#F5F3F0] transition-colors"
                        data-testid={`dash-upgrade-${k}`}>
                        <div className="flex items-baseline justify-between gap-4">
                          <span className="chip opacity-70">{v.label}</span>
                          <span className="font-display text-2xl">{v.price}</span>
                        </div>
                        <div className="mt-2 chip inline-flex items-center gap-1" style={{ color: "var(--joh-accent)" }}>
                          Upgrade pakket <ArrowUpRight size={12}/>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : ent?.pack_credits > 0 ? (
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="font-display text-6xl text-[#0A0A0A]" data-testid="dash-credits">{ent.pack_credits}</div>
                <div className="chip text-[#4A4A4A] mt-2">Lessen op je strippenkaart</div>
              </div>
              <Link to="/join" className="btn" data-testid="dash-package-join">Word member <ArrowUpRight size={14}/></Link>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="font-display uppercase text-2xl md:text-3xl text-[#0A0A0A]" data-testid="dash-no-package">Nog geen pakket</div>
                <p className="text-[#4A4A4A] mt-2">Kies een membership of strippenkaart en start met trainen.</p>
              </div>
              <Link to="/join" className="btn" data-testid="dash-package-choose">Kies een pakket <ArrowUpRight size={14}/></Link>
            </div>
          )}
        </div>
      </section>

      {/* Mijn boekingen */}
      <section className="px-6 md:px-10 pb-24" data-testid="dash-bookings">
        <div className="chip text-[#4A4A4A] mb-2">— Mijn boekingen</div>
        <h2 className="font-display uppercase text-3xl md:text-5xl text-[#0A0A0A] mb-8">Aankomend & eerder.</h2>
        {loading ? <div className="chip text-[#4A4A4A]">Laden…</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="border border-[#0A0A0A] p-6 bg-[#EAE7E2]" data-testid="dash-upcoming">
              <div className="chip text-[#4A4A4A] mb-3">Aankomend ({upcoming.length})</div>
              {upcoming.length === 0 ? <div className="chip text-[#4A4A4A]">Geen boekingen.</div> : (
                <div className="line-t">
                  {upcoming.slice(0, 6).map((b, i) => (
                    <div key={i} className="line-b py-2.5 flex items-center gap-3">
                      <span className="chip text-[#4A4A4A] w-28">{b.class_date}</span>
                      <span className="text-sm font-semibold text-[#0A0A0A] flex-1">{b.class_type}</span>
                      <span className="chip text-[#4A4A4A]">{b.class_start}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-[#0A0A0A] p-6 bg-[#EAE7E2]" data-testid="dash-past">
              <div className="chip text-[#4A4A4A] mb-3">Eerdere trainingen ({past.length})</div>
              {past.length === 0 ? <div className="chip text-[#4A4A4A]">Nog geen eerdere trainingen.</div> : (
                <div className="line-t">
                  {past.slice(0, 6).map((b, i) => (
                    <div key={i} className="line-b py-2.5 flex items-center gap-3">
                      <span className="chip text-[#4A4A4A] w-28">{b.class_date}</span>
                      <span className="text-sm font-semibold text-[#0A0A0A] flex-1">{b.class_type}</span>
                      <span className={`chip ${b.attended === true ? "accent-ink" : b.attended === false ? "text-red-800" : "text-[#4A4A4A]"}`}>
                        {b.attended === true ? "Aanwezig" : b.attended === false ? "No-show" : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/mij" className="chip text-[#4A4A4A] accent-hover inline-flex items-center gap-1 mt-4" data-testid="dash-bookings-portal">
                Volledige historie in portal <ArrowUpRight size={12}/>
              </Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
